import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { PhoneCTA } from "@/components/ContactButtons";
import WhatsAppIcon from "@/components/WhatsAppIcon";
import { WHATSAPP_URL } from "@/lib/contact";
import { track } from "@/lib/track";

const MIN = 2500;
const MAX = 30000;
const STEP = 500;
const RATE = 0.04;

const fmt = (n) => new Intl.NumberFormat("fr-BE", { maximumFractionDigits: 0 }).format(n);

const Simulator = () => {
  const [amount, setAmount] = useState(10000);
  const used = useRef(false);
  const fee = Math.round(amount * RATE);
  const fill = ((amount - MIN) / (MAX - MIN)) * 100;

  const onChange = (e) => {
    setAmount(Number(e.target.value));
    if (!used.current) {
      used.current = true;
      track("slider_simulator_change");
    }
  };

  return (
    <section id="simulateur" data-testid="simulator-section" className="py-16 sm:py-24 lg:py-28 bg-[#0d2731] relative overflow-hidden scroll-mt-24">
      <div className="absolute inset-0 dotted-bg-light opacity-60" aria-hidden="true" />
      <div className="container-avelis relative">
        <div className="max-w-2xl">
          <p className="eyebrow !text-[#b5c4b1]"><span className="dot-sage" />Simulateur</p>
          <h2 className="mt-4 text-2xl sm:text-3xl lg:text-[2.6rem] font-extrabold tracking-tight leading-[1.15] text-[#f6f3ec]">
            Combien vous coûterait AVELIS ?
          </h2>
          <p className="mt-4 text-base sm:text-lg text-[#f6f3ec]/70 leading-relaxed">
            Déplacez le curseur pour estimer vos honoraires mensuels.
          </p>
        </div>

        <div className="mt-10 sm:mt-14 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-stretch">
          <div className="lg:col-span-7 flex flex-col justify-center rounded-3xl border border-[#f6f3ec]/12 bg-[#f6f3ec]/[0.04] backdrop-blur-sm p-7 sm:p-10">
            <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#b5c4b1]">
              Facturation mensuelle
            </p>
            <p data-testid="simulator-amount" className="mt-3 text-4xl sm:text-5xl font-extrabold tracking-tight text-[#f6f3ec] tabular-nums">
              {fmt(amount)} €
            </p>

            <div className="mt-8">
              <input
                type="range"
                min={MIN}
                max={MAX}
                step={STEP}
                value={amount}
                onChange={onChange}
                className="avelis-range"
                style={{ "--fill": `${fill}%` }}
                aria-label="Facturation mensuelle estimée en euros"
                aria-valuetext={`${fmt(amount)} euros`}
                data-testid="simulator-slider"
              />
              <div className="flex justify-between text-xs font-semibold text-[#f6f3ec]/50 tabular-nums">
                <span>{fmt(MIN)} €</span>
                <span>{fmt(MAX)} €</span>
              </div>
            </div>
          </div>

          <div className="lg:col-span-5">
            <motion.div
              data-testid="simulator-result"
              className="h-full rounded-3xl bg-[#f6f3ec] p-7 sm:p-10 flex flex-col"
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            >
              <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#176b70]">Honoraires AVELIS</p>
              <div className="mt-4 flex items-baseline gap-2 flex-wrap">
                <motion.span
                  key={fee}
                  initial={{ opacity: 0.4, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, ease: "easeOut" }}
                  data-testid="simulator-fee"
                  className="text-5xl sm:text-6xl font-extrabold tracking-tight text-[#0d2731] tabular-nums"
                >
                  {fmt(fee)} €
                </motion.span>
                <span className="text-sm font-bold text-[#4b585e]">TVAC par mois</span>
              </div>
              <p className="mt-3 text-sm text-[#4b585e] tabular-nums">
                {fmt(amount)} € × 4 % = <strong className="text-[#0d2731]">{fmt(fee)} € TVAC</strong>
              </p>
              <p className="mt-5 text-xs text-[#4b585e]/80 leading-relaxed border-t border-[#e5e1d8] pt-4">
                Estimation indicative calculée sur les honoraires effectivement encaissés issus des facturations traitées par AVELIS, hors frais de logiciel.
              </p>
              <div className="mt-auto pt-7 flex flex-col gap-3">
                <PhoneCTA location="simulator" label="Parler de ma facturation" className="w-full" />
                <a
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="cta-whatsapp-simulator"
                  onClick={() => track("click_whatsapp_simulator")}
                  className="inline-flex items-center justify-center gap-2 text-sm font-bold text-[#176b70] hover:text-[#0d2731] transition-colors duration-200 py-1"
                >
                  <WhatsAppIcon className="h-4 w-4" />
                  Poser une question sur WhatsApp
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Simulator;
